import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import { Button, Divider, Heading, Link, Spinner } from '@apollo/orbit';
import { Observatory } from '@apollo/space-kit/illustrations/Observatory';
import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import React, { useEffect, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import { useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { changelogRouteConfig, variantRouteConfig } from '../../routes';

import { ChangeLogSectionEntry } from './changeLogSectionEntry/ChangeLogSectionEntry';

const OVERFLOW_MARGIN = 10;

export const ChangeLogSection = ({
  schemaHistory,
  graphqlSchema,
  schemaHistoryLoading,
}: {
  graphqlSchema: GraphQLSchema | undefined;
  schemaHistory:
    | GraphQLTypes.VariantsHomepageQuery_service_variant_activeSchemaPublish_history[]
    | undefined;
  schemaHistoryLoading: boolean;
}) => {
  const historyItems = schemaHistory?.filter(
    (historyItem) => historyItem.diffToPrevious?.changes.length,
  );
  const location = useLocation();
  const { locationToSDL } = useInternalGraphLinking();

  const changelogEntryContainer = useRef<HTMLDivElement>(null);

  const [showMore, setShowMore] = useState<Boolean>(false);
  const [overflow, setOverflow] = useState<Boolean>(false);

  useEffect(() => {
    if (!historyItems?.length && showMore) {
      setShowMore(false);
    }
  }, [historyItems?.length, showMore]);

  return (
    <div>
      <div className="flex justify-between">
        <Heading size="lg">Changelog</Heading>
        <Tooltip label="See all entries" placement="left">
          <Link
            to={changelogRouteConfig.locationFrom({
              location,
              fromRouteConfig: variantRouteConfig,
            })}
            data-analytics-label="Sidebar: Changelog: See all entries"
            data-analytics-category="Variant Home"
          >
            View full changelog
          </Link>
        </Tooltip>
      </div>
      <Divider className="mt-1" />
      <div className="mt-3 flex h-full flex-col">
        <div>
          {schemaHistoryLoading ? (
            <div className="mt-4 flex justify-center">
              <Spinner />
            </div>
          ) : !historyItems?.length ? (
            <div className="mt-4 flex flex-col items-center whitespace-normal">
              <Observatory className="my-2 text-inverted" />
              <div className="flex flex-col items-start text-base">
                <p>
                  Your{' '}
                  <Link
                    to={locationToSDL({})}
                    data-analytics-label="Sidebar: Changelog: Blankslate: text:Published schema to:SDL"
                    data-analytics-category="Variant Home"
                  >
                    published schema
                  </Link>{' '}
                  has not changed since it was initially registered. Once
                  changes are published, return here to see what’s changed.
                </p>
                <p>
                  <Link
                    isExternal
                    to="https://www.apollographql.com/docs/studio/schema/schema-reporting/#registering-to-a-variant-with-schema-reporting"
                    data-analytics-label="Sidebar: Changelog: Blankslate: text:Learn more to:docs"
                    data-analytics-category="Variant Home"
                  >
                    Learn more
                  </Link>
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <ReactResizeDetector
                handleWidth
                handleHeight
                onResize={() => {
                  const curOverflow =
                    changelogEntryContainer?.current?.style.overflow;
                  if (changelogEntryContainer.current) {
                    if (curOverflow === 'visible')
                      changelogEntryContainer.current.style.overflow = 'hidden';
                    const isOverflowing =
                      changelogEntryContainer.current.clientHeight <
                        changelogEntryContainer.current.scrollHeight &&
                      changelogEntryContainer.current.scrollHeight -
                        changelogEntryContainer.current.clientHeight >
                        OVERFLOW_MARGIN;
                    if (isOverflowing) setOverflow(true);
                  }
                }}
              >
                <div
                  className={classnames(
                    'min-h-36 truncate',
                    showMore ? 'max-h-[800px]' : 'max-h-64',
                  )}
                  style={{
                    height: changelogEntryContainer.current?.scrollHeight,
                  }}
                  ref={changelogEntryContainer}
                >
                  {historyItems.map(
                    (
                      historyItem: GraphQLTypes.SchemaTagDiffFragment,
                      i: number,
                    ) => (
                      <li
                        key={historyItem.id}
                        className={classnames('flex', i > 0 && 'mt-16')}
                      >
                        {graphqlSchema && (
                          <ChangeLogSectionEntry
                            historyItem={historyItem}
                            graphqlSchema={graphqlSchema}
                            limitLines={800 / 20}
                          />
                        )}
                      </li>
                    ),
                  )}
                </div>
              </ReactResizeDetector>
              {overflow && (
                <div>
                  <Button
                    rightIcon={
                      showMore ? (
                        <IconChevronUp className="w-2" />
                      ) : (
                        <IconChevronDown className="w-2" />
                      )
                    }
                    variant="hidden"
                    onClick={() => setShowMore(!showMore)}
                    data-analytics-label={`Sidebar: Changelog: ${
                      showMore ? 'Show less' : 'Show all'
                    }`}
                    data-analytics-category="Variant Home"
                  >
                    {showMore ? 'Show less' : 'Show all'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
